/**
 * LocalTranscribe — the ONE client for the Mac mini's local Whisper service
 * (the `/local/voicemail-transcriptions` routes, served by server.js).
 *
 * ── Why this is shared ──────────────────────────────────────────────────────
 *
 * Two surfaces want the same thing — "transcribe this audio on the mini if the
 * mini is here, otherwise say so and let me fall back":
 *
 *   • js/voicemail-transcriber.js — the original, which owned all of this
 *     logic privately (health probe, upload, poll, backoff, deadline).
 *   • js/call-hub-upload.js — added Sep 2026, because on the Tailscale PWA
 *     EVERY `/api/*` call executes on the mini (server.js maps a route to
 *     ./api/<route>.js and runs it, with no proxy to Vercel), and the mini's
 *     .env has no DEEPGRAM_API_KEY. Manual Call Hub uploads therefore failed
 *     there with "DEEPGRAM_API_KEY is not configured." while the same upload
 *     worked on altech.agency.
 *
 * Copying the poll loop into the second caller is how this codebase has been
 * bitten before — four drifting HawkSoft link builders, two CGL stat counters
 * that disagreed in the wild. So it lives here once and both callers forward.
 *
 * ── Contract ────────────────────────────────────────────────────────────────
 *
 *   health({force})       → { available, backend, modelLabel, maxBytes, hint }
 *   isAvailable(file?)    → boolean  (also size-checks against maxBytes)
 *   transcribe(file, opts)→ { ok: true, transcript, backend, modelLabel }
 *                         | { ok: false, reason, message }
 *
 * It NEVER throws and it never falls back on the caller's behalf — a miss is a
 * value, so the Call Hub can hand the job to Deepgram and the voicemail tool
 * can hand it to in-browser WASM, each on its own terms.
 *
 * On the hosted app the SPA rewrite answers `/local/*` with index.html, so the
 * health probe's `res.json()` throws and is caught within 1200 ms. That is the
 * designed off-switch — there is no per-deployment flag to keep in step.
 *
 * ── remote: the Mac from altech.agency (October 2026) ──────────────────────
 *
 * A caller that passes `{ remote: true }` (only the voicemail tool) is sent to
 * the Mac's Tailscale address, HOSTED_MAC_URL, when the page itself is not
 * served by a Mac. A computer running Tailscale reaches it; on any other
 * computer the name does not resolve and the probe misses at once, so the
 * caller falls back. vercel.json lists the address in connect-src and
 * server.js answers altech.agency's CORS on the voicemail routes; both are
 * pinned to this constant by tests/voicemail-hosted-mac.test.js. The Call Hub
 * upload does not ask: on altech.agency it keeps Deepgram, whose speaker
 * labels a two-party call needs. The two answers are memoised apart.
 */
window.LocalTranscribe = (() => {
    'use strict';

    const root = window;

    const HEALTH_PATH = '/local/voicemail-transcriptions/health';
    const JOBS_PATH = '/local/voicemail-transcriptions';
    const HEALTH_TIMEOUT_MS = 1200;
    const HEALTH_TTL_MS = 30000;
    const UPLOAD_TIMEOUT_MS = 30000;
    const STATUS_TIMEOUT_MS = 10000;
    const POLL_INTERVAL_MS = 1000;
    const POLL_TIMEOUT_MS = 15 * 60 * 1000;
    // Consecutive status-poll failures tolerated before giving up (A-093). A
    // dropped Wi-Fi frame or the mini's server restarting is transient and the
    // JOB keeps running regardless, but one of them used to abandon the whole
    // transcription and lose the upload. POLL_TIMEOUT_MS still bounds it, so
    // this cannot spin forever against a genuinely dead server.
    const POLL_MAX_FAILURES = 3;
    const DEFAULT_MAX_BYTES = 25 * 1024 * 1024;
    const HOSTED_MAC_URL = 'https://sanctuarys-mac-mini.tail188d7c.ts.net';

    /** Why a transcription did not happen. Callers branch on these, never on prose. */
    const FAIL_REASONS = Object.freeze({
        UNAVAILABLE: 'unavailable',   // not on the mini (the normal case on altech.agency)
        TOO_LARGE: 'too_large',
        REJECTED: 'rejected',         // the server refused the upload
        FAILED: 'failed',             // the job ran and errored
        EMPTY: 'empty',               // job finished with nothing usable
        TIMEOUT: 'timeout',
        NETWORK: 'network',
    });

    // One memo per base URL: { at, result, promise }. '' is the page's own origin.
    const _healthByBase = new Map();

    function _fail(reason, message) {
        return { ok: false, reason, message: message || '' };
    }

    /** True when the page is served by a Mac or a dev box (the Tailscale app,
     *  localhost, the desktop app), whose own /local/* routes are the Mac's. */
    function _servedLocally() {
        const host = String((root.location && root.location.hostname) || '').toLowerCase();
        return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
            || host.endsWith('.localhost') || host.endsWith('.ts.net');
    }

    /** Same override chain the voicemail transcriber has always used, so a
     *  configured mini URL keeps working for BOTH callers rather than only the
     *  one that happened to own the logic. Then, for a `remote` caller on the
     *  hosted app, the Mac's Tailscale address. */
    function baseUrl(options) {
        const fromGlobal = root.__ALTECH_VOICEMAIL_SERVER_URL;
        if (fromGlobal) return String(fromGlobal).replace(/\/+$/, '');
        try {
            const key = (root.STORAGE_KEYS && root.STORAGE_KEYS.VOICEMAIL_SERVER_URL) || 'altech_voicemail_server_url';
            const stored = root.localStorage && root.localStorage.getItem(key);
            if (stored) return String(stored).replace(/\/+$/, '');
        } catch (_) { /* no storage: fall through */ }
        if (options && options.remote && !_servedLocally()) return HOSTED_MAC_URL;
        return '';
    }

    function _absolute(url, base) {
        if (!url) return '';
        return /^https?:\/\//i.test(url) ? url : `${base}${url}`;
    }

    function _canTry() {
        if (root.__ALTECH_DISABLE_SERVER_TRANSCRIPTION === true) return false;
        if (typeof root.fetch !== 'function') return false;
        const protocol = root.location && root.location.protocol;
        return protocol === 'http:' || protocol === 'https:'; // never file://
    }

    async function _timedFetch(url, options, timeoutMs) {
        const controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
        const timer = controller ? root.setTimeout(() => controller.abort(), timeoutMs) : null;
        try {
            return await root.fetch(url, {
                ...(options || {}),
                signal: controller ? controller.signal : (options && options.signal),
            });
        } finally {
            if (timer) root.clearTimeout(timer);
        }
    }

    function _delay(ms) {
        return new Promise((resolve) => root.setTimeout(resolve, ms));
    }

    /** { available, backend?, modelLabel?, maxBytes?, hint? } — memoised 30 s
     *  per base URL, in-flight deduped, never throws. `remote`: see the header. */
    async function health(options) {
        const opts = options || {};
        if (!_canTry()) return { available: false };
        const base = baseUrl(opts);
        let memo = _healthByBase.get(base);
        if (!memo) {
            memo = { at: 0, result: null, promise: null };
            _healthByBase.set(base, memo);
        }
        const now = Date.now();
        if (!opts.force && memo.result && (now - memo.at) < HEALTH_TTL_MS) return memo.result;
        if (memo.promise) return memo.promise;

        memo.promise = _timedFetch(`${base}${HEALTH_PATH}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        }, opts.timeoutMs || HEALTH_TIMEOUT_MS)
            .then(async (response) => {
                if (!response || !response.ok) return { available: false };
                // On the hosted app this is index.html, so the parse throws.
                const data = await response.json().catch(() => ({}));
                // A bare { available: false } on the miss: an unavailable
                // server has no model label or size limit, and filling in
                // defaults there would hand a caller capability data about a
                // machine that never answered.
                if (data.available !== true) return { available: false };
                return {
                    available: true,
                    backend: data.backend || 'local-cli',
                    modelLabel: data.modelLabel || 'Fast server Whisper',
                    maxBytes: Number(data.maxBytes) || DEFAULT_MAX_BYTES,
                    hint: data.hint || '',
                };
            })
            .catch(() => ({ available: false }))
            .then((result) => {
                memo.result = result;
                memo.at = Date.now();
                return result;
            })
            .finally(() => { memo.promise = null; });

        return memo.promise;
    }

    /** Cheap "should I even try?" — also refuses a file the server would reject. */
    async function isAvailable(file, options) {
        const h = await health(options);
        if (!h || !h.available) return false;
        if (file && h.maxBytes && Number(file.size || 0) > h.maxBytes) return false;
        return true;
    }

    /**
     * Upload + poll to completion.
     *
     * @param {File|Blob} file
     * @param {{ onProgress?: (pct:number, stage:string) => void, remote?: boolean }} [options]
     * @returns {Promise<{ok:true,transcript:string,backend:string,modelLabel:string}
     *                  |{ok:false,reason:string,message:string}>}
     */
    async function transcribe(file, options) {
        const opts = options || {};
        const onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : () => {};
        const base = baseUrl(opts);

        if (!file) return _fail(FAIL_REASONS.UNAVAILABLE, 'No audio file.');
        const h = await health(opts);
        if (!h || !h.available) {
            return _fail(FAIL_REASONS.UNAVAILABLE, 'Local transcription server is not available.');
        }
        if (h.maxBytes && Number(file.size || 0) > h.maxBytes) {
            return _fail(FAIL_REASONS.TOO_LARGE, 'Recording is larger than the local server accepts.');
        }

        const start = Date.now();
        onProgress(12, 'uploading');

        let created;
        try {
            const upload = await _timedFetch(`${base}${JOBS_PATH}`, {
                method: 'POST',
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'X-File-Name': encodeURIComponent(file.name || 'recording.wav'),
                },
                body: file,
            }, UPLOAD_TIMEOUT_MS);
            created = await upload.json().catch(() => ({}));
            if (!upload.ok) {
                return _fail(FAIL_REASONS.REJECTED, created.error || 'Local server rejected the recording.');
            }
        } catch (err) {
            return _fail(FAIL_REASONS.NETWORK, (err && err.message) || 'Could not reach the local server.');
        }

        const statusUrl = _absolute(created.statusUrl || (created.id ? `${JOBS_PATH}/${created.id}` : ''), base);
        if (!statusUrl) return _fail(FAIL_REASONS.REJECTED, 'Local server did not return a job id.');

        onProgress(25, 'working');

        // The fetch/parse lives in its own try so a TRANSPORT failure can be
        // retried, while the two genuinely fatal outcomes — status 'error' and
        // the overall deadline — stay outside it. Inside, a retry would swallow
        // a real server-side failure and keep polling a job that already died.
        let pollFailures = 0;
        while (Date.now() - start < POLL_TIMEOUT_MS) {
            let status = null;
            try {
                const response = await _timedFetch(statusUrl, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                }, STATUS_TIMEOUT_MS);
                const parsed = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(parsed.error || 'Could not read local transcription status.');
                status = parsed;
            } catch (pollErr) {
                pollFailures += 1;
                if (pollFailures >= POLL_MAX_FAILURES) {
                    return _fail(FAIL_REASONS.NETWORK, (pollErr && pollErr.message) || 'Lost contact with the local server.');
                }
                await _delay(POLL_INTERVAL_MS * pollFailures); // linear backoff
                continue;
            }
            pollFailures = 0;

            if (typeof status.progress === 'number') {
                onProgress(Math.max(25, Math.min(95, status.progress)), 'working');
            }
            if (status.status === 'done') {
                const text = String(status.transcript || status.text || '');
                if (!text.trim()) return _fail(FAIL_REASONS.EMPTY, 'Local server returned an empty transcript.');
                return {
                    ok: true,
                    transcript: text,
                    backend: status.backend || h.backend || 'local-cli',
                    modelLabel: status.modelLabel || h.modelLabel || 'Fast server Whisper',
                };
            }
            if (status.status === 'error') {
                return _fail(FAIL_REASONS.FAILED, status.error || 'Local transcription failed.');
            }
            await _delay(POLL_INTERVAL_MS);
        }

        return _fail(FAIL_REASONS.TIMEOUT, 'Local transcription timed out.');
    }

    /** Drops the memoised health so a test (or a retry after the mini comes
     *  back) re-probes instead of waiting out the 30 s TTL. */
    function resetHealth() {
        _healthByBase.clear();
    }

    return {
        health,
        isAvailable,
        transcribe,
        baseUrl,
        resetHealth,
        FAIL_REASONS,
        HEALTH_PATH,
        JOBS_PATH,
        HOSTED_MAC_URL,
    };
})();
